import { ReactSVG } from "react-svg";
import { classNames } from "helpers";
import Tooltip from "ui/components/Tooltip/Tooltip";

type TableIconSize = "small" | "medium" | "large" | "xlarge";

interface TableIconProps {
  imageUrl?: string | null;
  initials?: string;
  icon?: string;
  /** Predefinirana veličina. Default: "medium" (w-12 h-12). */
  size?: TableIconSize;
  /**
   * Override Tailwind klase za container (npr. "w-20 h-12" za pravokutnu sliku vozila).
   * Ako je postavljen, ignorira `size`.
   */
  className?: string;
  /** Image fit: "cover" (default — kropa) ili "contain" (cijela slika vidljiva, može imati prazninu). */
  imageFit?: "cover" | "contain";
  /** Prikaži veliku verziju slike u tooltip-u na hover (samo ako postoji imageUrl). */
  showThumbnail?: boolean;
}

const sizeClasses: Record<TableIconSize, string> = {
  small: "w-8 h-8",
  medium: "w-12 h-12",
  large: "w-16 h-16",
  xlarge: "w-20 h-20",
};

const iconSizeClasses: Record<TableIconSize, string> = {
  small: "w-4 h-4",
  medium: "w-5 h-5",
  large: "w-7 h-7",
  xlarge: "w-10 h-10",
};

const TableIcon: React.FC<TableIconProps> = ({
  imageUrl,
  initials,
  icon,
  size = "medium",
  className,
  imageFit = "contain",
  showThumbnail = false,
}) => {
  const hasImage = !!imageUrl;
  const containerClass = className ?? sizeClasses[size];
  const iconClass = iconSizeClasses[size];

  const iconElement = (
    <div className={classNames("bg-slate-50 rounded-full block relative overflow-hidden", containerClass)}>
      {hasImage ? (
        <div className="w-full h-full">
          <img
            src={imageUrl}
            alt=""
            className={classNames(
              "w-full h-full rounded-full",
              imageFit === "cover" ? "object-cover" : "object-contain"
            )}
          />
        </div>
      ) : icon ? (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <ReactSVG src={icon} className={classNames("[&_svg]:stroke-current text-slate-500", iconClass)} />
        </div>
      ) : (
        <span className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          {initials}
        </span>
      )}
    </div>
  );

  if (showThumbnail && hasImage) {
    return (
      <Tooltip position="bottom-right" trigger={iconElement}>
        <img
          src={imageUrl}
          alt=""
          className="block w-64 h-auto rounded"
        />
      </Tooltip>
    );
  }

  return iconElement;
};

export default TableIcon;